import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, ChevronDown, Clock } from 'lucide-react'
import { ExerciseSummaryRow } from './ExerciseSummaryRow'
import { cn } from '../../lib/utils'
import {
  groupExercisesByFamily,
  isSingleton,
  variantLabel,
  type ExerciseFamily,
} from './exercise-families'
import type { Exercise } from '../../types/exercise'

interface ExerciseResultsListProps {
  exercises: Exercise[]
  selectedIds: string[]
  onToggle: (exercise: Exercise) => void
}

/**
 * The result list shared by the diary's AddWorkoutDialog and the plan builder's
 * ExercisePickerDialog. Rows that belong to a family collapse into one card
 * that opens on its variants; everything else renders as a plain row.
 */
export function ExerciseResultsList({ exercises, selectedIds, onToggle }: ExerciseResultsListProps) {
  const [expanded, setExpanded] = useState<string[]>([])
  const families = groupExercisesByFamily(exercises)

  function toggleExpanded(key: string) {
    setExpanded((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]))
  }

  return (
    <>
      {families.map((family) =>
        isSingleton(family) ? (
          <SingleExerciseRow
            key={family.key}
            exercise={family.lead}
            selected={selectedIds.includes(family.lead.id)}
            onToggle={onToggle}
          />
        ) : (
          <FamilyCard
            key={family.key}
            family={family}
            selectedIds={selectedIds}
            open={expanded.includes(family.key)}
            onToggleOpen={() => toggleExpanded(family.key)}
            onToggle={onToggle}
          />
        )
      )}
    </>
  )
}

function SingleExerciseRow({
  exercise,
  selected,
  onToggle,
}: {
  exercise: Exercise
  selected: boolean
  onToggle: (exercise: Exercise) => void
}) {
  return (
    <button
      type="button"
      onClick={() => onToggle(exercise)}
      aria-pressed={selected}
      className={cn(
        'rounded-md border p-2 text-left transition-colors',
        selected ? 'border-primary bg-primary/5' : 'hover:bg-accent'
      )}
    >
      <ExerciseSummaryRow
        exercise={exercise}
        compact
        right={selected ? <Check className="size-4 text-primary" /> : null}
      />
    </button>
  )
}

function FamilyCard({
  family,
  selectedIds,
  open,
  onToggleOpen,
  onToggle,
}: {
  family: ExerciseFamily
  selectedIds: string[]
  open: boolean
  onToggleOpen: () => void
  onToggle: (exercise: Exercise) => void
}) {
  const { t } = useTranslation()
  const mPrefix = t('common.minutesShort', 'm')
  const selectedCount = family.variants.filter((v) => selectedIds.includes(v.id)).length

  return (
    <div
      className={cn(
        'rounded-md border transition-colors',
        selectedCount > 0 ? 'border-primary bg-primary/5' : ''
      )}
    >
      <button
        type="button"
        onClick={onToggleOpen}
        aria-expanded={open}
        className="w-full flex items-center gap-3 min-w-0 p-2 text-left rounded-md hover:bg-accent transition-colors"
      >
        {family.lead.image_url ? (
          <img
            src={family.lead.image_url}
            alt=""
            className="size-10 rounded-md object-cover bg-muted shrink-0"
            loading="lazy"
          />
        ) : (
          <div className="size-10 rounded-md bg-muted shrink-0" />
        )}

        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground truncate">
            {family.lead.category.name}
          </span>
          <span className="text-sm font-medium truncate">{family.name}</span>
          <span className="text-xs text-muted-foreground">
            {t('trainingDiary.workouts.dialog.variantCount', {
              count: family.variants.length,
              defaultValue: '{{count}} variants',
            })}
            {selectedCount > 0 && (
              <span className="text-primary">
                {' · '}
                {t('trainingDiary.workouts.dialog.variantsSelected', {
                  count: selectedCount,
                  defaultValue: '{{count}} selected',
                })}
              </span>
            )}
          </span>
        </div>

        <ChevronDown
          className={cn(
            'size-4 shrink-0 text-muted-foreground transition-transform',
            open && 'rotate-180'
          )}
        />
      </button>

      {open && (
        <ul className="flex flex-col gap-1 border-t p-2">
          {family.variants.map((variant) => {
            const selected = selectedIds.includes(variant.id)
            return (
              <li key={variant.id}>
                <button
                  type="button"
                  onClick={() => onToggle(variant)}
                  aria-pressed={selected}
                  className={cn(
                    'w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                    selected ? 'bg-primary/10 text-primary' : 'hover:bg-accent'
                  )}
                >
                  <span className="flex-1 truncate">{variantLabel(variant)}</span>
                  {variant.duration_minutes != null && (
                    <span className="shrink-0 text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="size-3" />
                      {variant.duration_minutes}
                      {mPrefix}
                    </span>
                  )}
                  {selected && <Check className="size-4 shrink-0 text-primary" />}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
